import { useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Bell, ChevronDown, ChevronRight } from 'lucide-react'
import { decisionTypeLabel } from '@/lib/decision-utils'
import { timeAgo } from '@/lib/utils'
import type { Decision } from '@/lib/api'

interface AcknowledgeSectionProps {
  decisions: Decision[]
  /** Acknowledging clears the item — wired to the approve mutation. */
  onAcknowledge: (id: string) => void
  onAcknowledgeAll: (ids: string[]) => void
  busy: boolean
  exitingIds?: Set<string>
}

/**
 * FYI / informational decisions ("no data" alerts, routine health pings,
 * analytics notes) carry no real action — they only need acknowledging. They
 * live here, collapsed by default, so they never pollute the actionable queue.
 * "Acknowledge" resolves them via the same approve path that clears a decision.
 */
export function AcknowledgeSection({
  decisions,
  onAcknowledge,
  onAcknowledgeAll,
  busy,
  exitingIds,
}: AcknowledgeSectionProps) {
  const [expanded, setExpanded] = useState(false)
  const [armed, setArmed] = useState(false)

  if (decisions.length === 0) return null
  const ids = decisions.map((d) => d.id)

  function fireAll() {
    if (!armed) {
      setArmed(true)
      return
    }
    onAcknowledgeAll(ids)
    setArmed(false)
  }

  return (
    <div className="bg-cmd-surface/40">
      <div className="flex flex-wrap items-center justify-between gap-2 bg-cmd-low px-3 py-2">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex items-center gap-2"
          aria-expanded={expanded}
        >
          {expanded ? (
            <ChevronDown className="h-4 w-4 text-cmd-outline" aria-hidden="true" />
          ) : (
            <ChevronRight className="h-4 w-4 text-cmd-outline" aria-hidden="true" />
          )}
          <Bell className="h-3.5 w-3.5 text-cmd-outline" aria-hidden="true" />
          <h3 className="font-label text-[0.7rem] font-semibold tracking-widest text-cmd-outline uppercase">
            For information
          </h3>
          <Badge variant="outline" className="text-[9px] px-1 py-0">
            {decisions.length}
          </Badge>
        </button>
        <div className="flex items-center gap-1">
          {armed && (
            <Button
              size="sm"
              variant="ghost"
              disabled={busy}
              onClick={() => setArmed(false)}
              className="font-label text-[0.65rem] tracking-wider"
            >
              CANCEL
            </Button>
          )}
          <Button
            size="sm"
            variant="secondary"
            disabled={busy}
            onClick={fireAll}
            className="font-label text-[0.65rem] tracking-wider"
          >
            {armed ? `CONFIRM · ACK ${decisions.length}` : `ACKNOWLEDGE ALL (${decisions.length})`}
          </Button>
        </div>
      </div>

      {expanded && (
        <div className="space-y-0">
          {decisions.map((d, i) => (
            <div
              key={d.id}
              className={`flex items-start gap-3 p-3 transition-all duration-300 ${
                i % 2 === 0 ? 'bg-cmd-surface' : 'bg-cmd-low'
              } ${exitingIds?.has(d.id) ? 'pointer-events-none -translate-x-2 opacity-0' : ''}`}
            >
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm text-white/90">{d.summary}</p>
                <p className="mt-0.5 font-mono text-[0.6rem] text-cmd-outline">
                  {decisionTypeLabel(d.decisionType)} · {d.skillName} · {timeAgo(d.createdAt)}
                </p>
              </div>
              <Button
                size="sm"
                variant="ghost"
                disabled={busy}
                onClick={() => onAcknowledge(d.id)}
                className="shrink-0 font-label text-[0.6rem] tracking-wider"
              >
                ACK
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
